/**
 * Module-level singleton registry for WebSocket instant event handlers.
 *
 * Why a singleton and not a React hook?
 * `useRealtimeSync()` is called from multiple components, each creating
 * an isolated `useRef` with its own `instantHandlers` Map. Handlers
 * registered in one component's Map are invisible to `executeInstantHandlers`
 * running in another component's Map. A module-level Map guarantees all
 * registrations and lookups share the same storage.
 */

import { wsLogger } from '@/shared/lib/logger';
import type { DataChangedEvent, InstantHandler } from './realtime-sync.types';

const instantHandlers = new Map<string, Set<InstantHandler>>();

/**
 * Register an instant handler for a specific entity.
 * Returns a cleanup function that removes the handler.
 */
export function onInstant(
  entity: string,
  handler: InstantHandler
): () => void {
  if (!instantHandlers.has(entity)) {
    instantHandlers.set(entity, new Set());
  }

  instantHandlers.get(entity)!.add(handler);

  return () => {
    const handlers = instantHandlers.get(entity);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        instantHandlers.delete(entity);
      }
    }
  };
}

/**
 * Remove all registered handlers. Called on WebSocketProvider unmount
 * to prevent duplicate registrations on remount.
 */
export function clearAllHandlers(): void {
  instantHandlers.clear();
}

/**
 * Execute all registered instant handlers for an event's entity.
 */
export async function executeInstantHandlers(
  event: DataChangedEvent
): Promise<void> {
  const handlers = instantHandlers.get(event.entity);

  if (!handlers || handlers.size === 0) {
    wsLogger.debug('[RealtimeSync] No instant handlers for', event.entity);
    return;
  }

  const promises: Promise<void>[] = [];

  handlers.forEach((handler) => {
    try {
      const result = handler(event);
      if (result instanceof Promise) {
        promises.push(result);
      }
    } catch (error) {
      wsLogger.error('[RealtimeSync] Instant handler error:', error);
    }
  });

  if (promises.length > 0) {
    await Promise.allSettled(promises);
  }
}
